import { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { useStore } from '@/store/main';
import cn from 'classnames';
import PuntosHopBtn from '../Botones/PuntosHop';
import HopName from '../Svgs/hopname';
import styles from './index.module.scss';
import { useRouter } from 'next/router';
import Menu from '../Menu';
import { useIsomorphicLayoutEffect } from '@/helpers/isomorphicEffect';

const portalesChildren = [
  { name: 'Portal Clientes', url: 'https://clientes.hopenvios.net/', external: true },
  { name: 'Portal Autogestión Casos', url: 'https://hopsellersposventa.powerappsportals.com/', external: true }
];

const menuDict = {
  'es-AR': {
    links: [
      {
        name: 'Inicio',
        url: '/'
      },
      {
        name: 'Quiero ser Punto HOP',
        url: '/quiero-ser-punto-hop'
      },
      {
        name: 'HOP para tu negocio',
        url: '/hop-para-tu-negocio'
      },
      {
        name: 'Conocenos',
        url: '/conocenos'
      },
      {
        name: 'Contacto',
        url: '/contacto'
      },
      {
        name: 'Paquetería',
        url: '/paqueteria'
      },
      {
        name: 'Portales',
        children: portalesChildren
      }
    ],
    rastrea: "Rastreá tu envío",
    conoce: "Conocé los puntos HOP"
  },
  'es-PE': {
    links: [
      {
        name: 'Inicio',
        url: '/'
      },
      {
        name: 'Quiero ser Punto HOP',
        url: '/quiero-ser-punto-hop'
      },
      {
        name: 'HOP para tu negocio',
        url: '/hop-para-tu-negocio'
      },
      {
        name: 'Conócenos',
        url: '/conocenos'
      },
      {
        name: 'Contacto',
        url: '/contacto'
      },
      {
        name: 'Paquetería',
        url: '/paqueteria'
      },
      {
        name: 'Portales',
        children: portalesChildren
      }
    ],
    rastrea: "Rastrea tu envío",
    conoce: "Conocé los puntos HOP"
  },
  'es-UY': {
    links: [
      {
        name: 'Inicio',
        url: '/'
      },
      {
        name: 'Quiero ser Punto HOP',
        url: '/quiero-ser-punto-hop'
      },
      {
        name: 'HOP para tu negocio',
        url: '/hop-para-tu-negocio'
      },
      {
        name: 'Conocenos',
        url: '/conocenos'
      },
      {
        name: 'Contacto',
        url: '/contacto'
      },
      {
        name: 'Paquetería',
        url: '/paqueteria'
      },
      {
        name: 'Portales',
        children: portalesChildren
      }
    ],
    rastrea: "Rastreá tu envío",
    conoce: "Conocé los puntos HOP"
  }
}

const Header = ({ locale }) => {
  const menu = menuDict[locale] || menuDict['es-AR'];
  const [menuLinks] = useStore((state) => [state.menuLinks]);
  const [isMenuOpen, setIsMenuOpen] = useStore((state) => [state.isMenuOpen, state.setIsMenuOpen]);
  const [hidden, setHidden] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const lastScroll = useRef(0);
  const scrollDirection = useRef(0);
  const scrollDistance = useRef(0);
  const navRef = useRef(null);

  const router = useRouter();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  useEffect(() => {
    if (openDropdown === null) return;
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  useEffect(() => {
    const close = () => setOpenDropdown(null);
    router.events.on('routeChangeStart', close);
    return () => router.events.off('routeChangeStart', close);
  }, [router.events]);

  useIsomorphicLayoutEffect(() => {
    let animationFrame = null;
    lastScroll.current = window.scrollY;

    const handleScroll = () => {
      if (animationFrame !== null) return;

      animationFrame = window.requestAnimationFrame(() => {
        animationFrame = null;
        const y = window.scrollY;
        const delta = y - lastScroll.current;

        if (y <= 80) {
          setHidden(false);
          scrollDistance.current = 0;
          scrollDirection.current = 0;
        } else {
          if (delta !== 0) {
            const direction = Math.sign(delta);
            if (direction !== scrollDirection.current) {
              scrollDirection.current = direction;
              scrollDistance.current = 0;
            }
            scrollDistance.current += Math.abs(delta);
            if (scrollDistance.current >= 8) setHidden(direction > 0);
          }
        }

        lastScroll.current = y;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
    };
  }, []);

  return (
    <header
      className={cn(
        'site-head fixed top-0 sm:top-20 left-1/2 -translate-x-1/2 w-full pt-20 sm:pt-0 z-[1000] bg-white sm:bg-transparent duration-40',
        {
          '-translate-y-[130%]': hidden
        }
      )}
    >
      <div className="site-grid relative w-full">
        <div className="col-span-12 flex items-center justify-between md:justify-center pr-15 sm:pr-0 pb-4 sm:pb-0">
          <Link href="/" locale={locale} className="relative z-[1100] inline-block w-100 sm:w-115" aria-label="Ir a la página de inicio de HOP">
            <HopName />
          </Link>
          <nav
            ref={navRef}
            className={cn('hidden md:block px-12 py-4 bg-white rounded-full mx-10', styles.shadow)}
          >
            <ul className="flex items-center">
              {menu.links.map((link, index) => {
                const hasChildren = Array.isArray(link.children) && link.children.length > 0;
                const isOpen = openDropdown === index;

                if (hasChildren) {
                  return (
                    <li key={index} className="relative">
                      <button
                        type="button"
                        onClick={() => setOpenDropdown(isOpen ? null : index)}
                        aria-haspopup="menu"
                        aria-expanded={isOpen}
                        className={cn('relative inline-flex items-center py-4 px-10', {
                          'is-active ': isOpen
                        })}
                        aria-label={`Abrir menú ${link.name}`}
                      >
                        <p className="p3 !leading-[3] !font-medium text-black opacity-60 uppercase navLink whitespace-nowrap">
                          {link.name}
                        </p>
                        <svg
                          className={cn('w-10 h-auto ml-6 duration-40', { 'rotate-180': isOpen })}
                          viewBox="0 0 10 6"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M1 1L5 5L9 1" stroke="#1d1d1b" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                      <div
                        className={cn(
                          'absolute left-1/2 -translate-x-1/2 top-full mt-12 min-w-[22rem] bg-white rounded-20 py-12 duration-40 origin-top',
                          styles.shadow,
                          {
                            'opacity-0 pointer-events-none scale-95': !isOpen,
                            'opacity-100 scale-100': isOpen
                          }
                        )}
                        role="menu"
                      >
                        <ul>
                          {link.children.map((child, ci) => (
                            <li key={ci}>
                              <Link
                                href={child.url}
                                locale={child.external ? undefined : locale}
                                target={child.external ? '_blank' : undefined}
                                rel={child.external ? 'noopener noreferrer' : undefined}
                                onClick={() => setOpenDropdown(null)}
                                className="block px-20 py-8 hover:bg-grey/20 duration-40"
                                role="menuitem"
                                aria-label={`Ir a ${child.name}`}
                              >
                                <p className="p3 !leading-[2] !font-medium text-black opacity-60 uppercase">
                                  {child.name}
                                </p>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </li>
                  );
                }

                return (
                  <li key={index} className="">
                    <Link
                      className={cn('relative inline-block py-4 px-10', {
                        'is-active ': router.pathname == link.url
                      })}
                      href={link.url}
                      locale={locale}
                      aria-label={`Ir a ${link.name}`}
                    >
                      <svg
                        className="w-8 h-auto absolute top-[45%] left-0 -translate-y-1/2 translate-x-15 opacity-0 duration-40"
                        viewBox="0 0 8 5"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3.28114 0.00262141C3.5122 0.0135243 3.73256 0.071152 3.947 0.148249C4.22568 0.248191 4.48746 0.378763 4.7405 0.524391C5.22629 0.803967 5.67714 1.12767 6.11898 1.46202C6.50756 1.75613 6.89558 2.05051 7.28444 2.34436C7.34446 2.38979 7.40758 2.43184 7.46929 2.47545C7.47943 2.48246 7.48901 2.49025 7.5 2.49855C7.3918 2.57695 7.28472 2.65197 7.18046 2.73062C6.85782 2.97438 6.53658 3.21995 6.21451 3.46448C5.81015 3.77131 5.40043 4.07165 4.96508 4.34059C4.65878 4.52956 4.34234 4.70219 4.00025 4.83095C3.70353 4.94257 3.3978 5.01344 3.07375 4.99786C2.71532 4.98073 2.37239 4.90649 2.04806 4.76319C1.64032 4.5833 1.30105 4.32683 1.03251 3.99196C0.769044 3.66358 0.603073 3.29653 0.534318 2.89365C0.463027 2.47441 0.502477 2.06271 0.655767 1.6619C0.822864 1.22476 1.10127 0.857961 1.48872 0.56281C1.81051 0.317759 2.17514 0.150586 2.58204 0.062846C2.81169 0.0132649 3.04501 -0.00828099 3.2803 0.00288129L3.28114 0.00262141Z"
                          fill="#064246"
                        />
                      </svg>

                      <p className="p3 !leading-[3] !font-medium text-black opacity-60 uppercase navLink whitespace-nowrap">
                        {link.name}
                      </p>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>
          <div
            className={cn(
              'hidden md:flex items-center justify-between pl-28 pr-7 py-7 bg-white rounded-full',
              styles.shadow
            )}
          >
            <div className="">
              <Link
                href="/segui-tu-envio"
                className={cn('relative flex items-center py-2 navLink-aside', {
                  'is-active ': router.pathname == '/segui-tu-envio'
                })}
                locale={locale}
                aria-label={`${menu.rastrea} - Seguir el estado de tu paquete`}
              >
                <svg
                  className="w-20 h-auto opacity-60 mr-5"
                  viewBox="0 0 20 21"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="11.3953"
                    cy="8.65308"
                    r="8.05151"
                    stroke="#1d1d1b"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <line
                    x1="0.654297"
                    y1="20.2577"
                    x2="5.85476"
                    y2="15.0573"
                    stroke="#1d1d1b"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <p className="p3 !leading-[3] !font-medium text-black opacity-60 uppercase whitespace-nowrap">
                  {menu.rastrea}
                </p>
              </Link>
            </div>

            <div className="w-1 h-20 bg-grey mx-16"></div>
            <div>
              <PuntosHopBtn text={menu.conoce} locale={locale} />
            </div>
          </div>
          <button
            onClick={toggleMenu}
            className="md:hidden burguer w-30 h-26 flex flex-col justify-between relative z-[1200]"
            aria-label={isMenuOpen ? "Cerrar menú de navegación" : "Abrir menú de navegación"}
          >
            <div
              className={cn('bar w-full h-[1px] bg-black duration-40 origin-left', {
                '!bg-white rotate-45 scale-x-[1.18]': isMenuOpen
              })}
            ></div>
            <div
              className={cn('bar w-full h-[1px] bg-black duration-40 origin-left', {
                '!bg-white opacity-0': isMenuOpen
              })}
            ></div>
            <div
              className={cn('bar w-full h-[1px] bg-black duration-40 origin-left', {
                '!bg-white -rotate-[45deg] scale-x-[1.2]': isMenuOpen
              })}
            ></div>
          </button>
        </div>
      </div>
      <Menu />
    </header>
  );
};

export default Header;
